// The shape step's entities — booking direct (manager+ on {businessId}),
// through the /api/booking proxy, exactly as the business app writes them
// (ihjizli-booking internal/http shape_*.go, Phase 1A). Onboarding never
// sends branch_id: the first branch is the anchor (ADR-0027 §2).
//
//   places            GET/POST /v1/business/{id}/places · PATCH …/places/{placeId}
//   price bands       GET/PUT  /v1/business/{id}/place-price-bands {bands:[{from_min,to_min,price_iqd}]} (replace-set, ≤ 12)
//   periods           GET/POST /v1/business/{id}/periods · PATCH …/periods/{periodId}

import { bookingApi, profileApi } from '@/lib/api';

export interface Place {
  id: string;
  business_id: string;
  name: string;
  active: boolean;
  pause_note?: string;
  sort_order: number;
}

export interface PriceBand {
  from_min: number;
  to_min: number;
  price_iqd: number;
}

export type PeriodDepositKind = 'percent' | 'fixed';

export interface Period {
  id: string;
  business_id: string;
  name: string;
  from_min: number;
  to_min: number;
  overnight: boolean;
  price_iqd: number;
  deposit_kind?: PeriodDepositKind;
  deposit_value?: number;
  active: boolean;
  sort_order: number;
}

export interface PeriodBody {
  name?: string;
  from_min?: number;
  to_min?: number;
  price_iqd?: number;
  deposit_kind?: PeriodDepositKind | '';
  deposit_value?: number | null;
  active?: boolean;
  sort_order?: number;
}

const base = (businessId: string) => `/v1/business/${encodeURIComponent(businessId)}`;
const opts = (businessId: string) => ({ businessId });

// ----------------------------------------------------------------- places --

export async function listPlaces(businessId: string): Promise<Place[]> {
  const res = await bookingApi.get<{ places?: Place[] }>(`${base(businessId)}/places`, undefined, opts(businessId));
  return Array.isArray(res?.places) ? res.places : [];
}

export async function createPlace(businessId: string, body: { name: string; sort_order?: number }): Promise<Place> {
  return bookingApi.post<Place>(`${base(businessId)}/places`, body, opts(businessId));
}

export async function patchPlace(businessId: string, placeId: string, body: { name?: string; active?: boolean }): Promise<Place> {
  return bookingApi.patch<Place>(`${base(businessId)}/places/${encodeURIComponent(placeId)}`, body, opts(businessId));
}

export async function getBands(businessId: string): Promise<PriceBand[]> {
  const res = await bookingApi.get<{ bands?: PriceBand[] }>(`${base(businessId)}/place-price-bands`, undefined, opts(businessId));
  return Array.isArray(res?.bands) ? res.bands : [];
}

export async function putBands(businessId: string, bands: PriceBand[]): Promise<PriceBand[]> {
  const res = await bookingApi.put<{ bands?: PriceBand[] }>(`${base(businessId)}/place-price-bands`, { bands }, opts(businessId));
  return Array.isArray(res?.bands) ? res.bands : bands;
}

// ---------------------------------------------------------------- periods --

export async function listPeriods(businessId: string): Promise<Period[]> {
  const res = await bookingApi.get<{ periods?: Period[] }>(`${base(businessId)}/periods`, undefined, opts(businessId));
  return Array.isArray(res?.periods) ? res.periods : [];
}

export async function createPeriod(businessId: string, body: PeriodBody): Promise<Period> {
  return bookingApi.post<Period>(`${base(businessId)}/periods`, body, opts(businessId));
}

export async function patchPeriod(businessId: string, periodId: string, body: PeriodBody): Promise<Period> {
  return bookingApi.patch<Period>(`${base(businessId)}/periods/${encodeURIComponent(periodId)}`, body, opts(businessId));
}

// ------------------------------------------------------------- unit types --
//   unit types        GET/POST /v1/business/{id}/unit-types · PATCH …/unit-types/{typeId}
//   seasons           PUT …/unit-types/{typeId}/seasons {seasons:[{from_date,to_date,price_iqd}]} (replace-set, ≤ 20)
//   scheduling        GET /v1/business/{id}/scheduling (weekend_nights for «الخميس والجمعة»)
//   photos            profile POST /v1/business/service-image (multipart "file") → {url}

export type PriceUnit = 'night' | 'day';
export type UnitDepositKind = 'percent' | 'fixed_per_night';

export interface UnitTypeSeason {
  id?: string;
  from_date: string;
  to_date: string;
  price_iqd: number;
}

export interface UnitType {
  id: string;
  business_id: string;
  name: string;
  /** «وصف قصير» (ruling H5, ≤ 120): the customer sees it under the type's name; booking omits it when empty. */
  description?: string;
  quantity: number;
  photos: string[];
  price_iqd: number;
  price_unit: PriceUnit;
  min_nights: number;
  check_in_min?: number;
  check_out_min?: number;
  weekend_price_iqd?: number;
  day_use_enabled: boolean;
  day_use_from_min?: number;
  day_use_to_min?: number;
  day_use_price_iqd?: number;
  deposit_kind?: UnitDepositKind;
  deposit_value?: number;
  active: boolean;
  sort_order: number;
  seasons: UnitTypeSeason[];
}

/** The create / PATCH body; null clears an optional field on PATCH. */
export interface UnitTypeBody {
  name?: string;
  /** ≤ 120 characters; on a PATCH "" clears it. */
  description?: string;
  quantity?: number;
  photos?: string[];
  price_iqd?: number;
  price_unit?: PriceUnit;
  min_nights?: number;
  check_in_min?: number | null;
  check_out_min?: number | null;
  weekend_price_iqd?: number | null;
  day_use_enabled?: boolean;
  day_use_from_min?: number | null;
  day_use_to_min?: number | null;
  day_use_price_iqd?: number | null;
  deposit_kind?: UnitDepositKind | null;
  deposit_value?: number | null;
  active?: boolean;
}

export async function listUnitTypes(businessId: string): Promise<UnitType[]> {
  const res = await bookingApi.get<{ unit_types?: UnitType[] }>(`${base(businessId)}/unit-types`, undefined, opts(businessId));
  return Array.isArray(res?.unit_types) ? res.unit_types.map((t) => ({ ...t, seasons: t.seasons ?? [], photos: t.photos ?? [] })) : [];
}

export async function createUnitType(businessId: string, body: UnitTypeBody): Promise<UnitType> {
  return bookingApi.post<UnitType>(`${base(businessId)}/unit-types`, body, opts(businessId));
}

export async function patchUnitType(businessId: string, typeId: string, body: UnitTypeBody): Promise<UnitType> {
  return bookingApi.patch<UnitType>(`${base(businessId)}/unit-types/${encodeURIComponent(typeId)}`, body, opts(businessId));
}

export async function putSeasons(businessId: string, typeId: string, seasons: UnitTypeSeason[]): Promise<UnitTypeSeason[]> {
  const res = await bookingApi.put<{ seasons?: UnitTypeSeason[] }>(
    `${base(businessId)}/unit-types/${encodeURIComponent(typeId)}/seasons`,
    { seasons: seasons.map(({ from_date, to_date, price_iqd }) => ({ from_date, to_date, price_iqd })) },
    opts(businessId),
  );
  return Array.isArray(res?.seasons) ? res.seasons : seasons;
}

/** The business's scheduling (owner read): the weekend nights the weekend price applies to (0 = Sunday … 6 = Saturday). */
export async function getWeekendNights(businessId: string): Promise<number[]> {
  const res = await bookingApi.get<{ weekend_nights?: number[] }>(`${base(businessId)}/scheduling`, undefined, opts(businessId));
  return Array.isArray(res?.weekend_nights) ? res.weekend_nights : [4, 5];
}

/** One unit photo: uploaded to profile's media store first; its https URL goes into `photos`. */
export async function uploadUnitPhoto(businessId: string, file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await profileApi.post<{ url?: string }>('/v1/business/service-image', form, opts(businessId));
  if (!res?.url) throw new Error('upload: no url');
  return res.url;
}

// --------------------------------------------------- programs + sessions --
//   programs          GET/POST /v1/business/{id}/programs
//   sessions          GET/POST /v1/business/{id}/programs/{programId}/sessions
//                     {starts_at, duration_min, seats, repeat_weekly, weeks?} → 201 {sessions:[…]}

export type BookingKind = 'session' | 'course';

export interface Program {
  id: string;
  business_id: string;
  name: string;
  trainer?: string;
  seat_price_iqd: number;
  booking_kind: BookingKind;
  meeting_point?: string;
  deposit_per_seat_iqd: number;
  active: boolean;
  sort_order: number;
  next_session_at?: string;
  upcoming_sessions: number;
}

export interface Session {
  id: string;
  program_id: string;
  starts_at: string;
  duration_min: number;
  seats: number;
  booked: number;
  remaining: number;
  status: string;
  series_id?: string;
}

export interface ProgramBody {
  name: string;
  trainer?: string;
  seat_price_iqd: number;
  booking_kind: BookingKind;
  meeting_point?: string;
  deposit_per_seat_iqd?: number;
}

export interface SessionsBody {
  starts_at: string;
  duration_min: number;
  seats: number;
  repeat_weekly: boolean;
  weeks?: number;
}

export async function listPrograms(businessId: string): Promise<Program[]> {
  const res = await bookingApi.get<{ programs?: Program[] }>(`${base(businessId)}/programs`, undefined, opts(businessId));
  return Array.isArray(res?.programs) ? res.programs : [];
}

export async function createProgram(businessId: string, body: ProgramBody): Promise<Program> {
  return bookingApi.post<Program>(`${base(businessId)}/programs`, body, opts(businessId));
}

/** Baghdad 'YYYY-MM-DD' of `now` + `days`. */
function baghdadDay(now: Date, days = 0): string {
  return new Date(now.getTime() + 3 * 3_600_000 + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * A program's sessions over the widest window booking serves (today .. +365,
 * its 366-day maximum) — the default is 84 days, which would hide a course or
 * a one-off session dated further out (the step then gates on the program's
 * own upcoming_sessions, not on this list).
 */
export async function listSessions(businessId: string, programId: string, now: Date = new Date()): Promise<Session[]> {
  const res = await bookingApi.get<{ sessions?: Session[] }>(
    `${base(businessId)}/programs/${encodeURIComponent(programId)}/sessions`,
    { from: baghdadDay(now), to: baghdadDay(now, 365) },
    opts(businessId),
  );
  return Array.isArray(res?.sessions) ? res.sessions : [];
}

export async function createSessions(businessId: string, programId: string, body: SessionsBody): Promise<Session[]> {
  const res = await bookingApi.post<{ sessions?: Session[] }>(`${base(businessId)}/programs/${encodeURIComponent(programId)}/sessions`, body, opts(businessId));
  return Array.isArray(res?.sessions) ? res.sessions : [];
}

// ----------------------------------------------------------------- tables --
//   tables            GET/POST /v1/business/{id}/tables · PATCH …/tables/{tableId}
//   the deposit       the scheduling PUT (table_deposit_enabled / _kind / _value), idempotent

export type TableZone = 'indoor' | 'outdoor' | 'family';

export interface Table {
  id: string;
  business_id: string;
  name: string;
  seats: number;
  zone: TableZone;
  sort_order: number;
  active: boolean;
}

export async function listTables(businessId: string): Promise<Table[]> {
  const res = await bookingApi.get<{ tables?: Table[] }>(`${base(businessId)}/tables`, undefined, opts(businessId));
  return Array.isArray(res?.tables) ? res.tables : [];
}

export async function createTable(businessId: string, body: { name: string; seats: number; zone: TableZone }): Promise<Table> {
  return bookingApi.post<Table>(`${base(businessId)}/tables`, body, opts(businessId));
}

export async function patchTable(businessId: string, tableId: string, body: { name?: string; seats?: number; active?: boolean }): Promise<Table> {
  return bookingApi.patch<Table>(`${base(businessId)}/tables/${encodeURIComponent(tableId)}`, body, opts(businessId));
}

/** The table deposit as the business's scheduling holds it (owner read). */
export async function getTableDeposit(businessId: string): Promise<{ enabled: boolean; kind?: 'per_person' | 'per_table'; value?: number }> {
  const res = await bookingApi.get<{ table_deposit_enabled?: boolean; table_deposit_kind?: string; table_deposit_value?: number }>(`${base(businessId)}/scheduling`, undefined, opts(businessId));
  const kind = res?.table_deposit_kind === 'per_person' || res?.table_deposit_kind === 'per_table' ? res.table_deposit_kind : undefined;
  return { enabled: !!res?.table_deposit_enabled, kind, value: typeof res?.table_deposit_value === 'number' ? res.table_deposit_value : undefined };
}
